import { useEffect } from "react";
import type { AppRoutes } from "@/types/domain";
import { popupCenter } from "@/utils/popup";
import { getRedirectUrl, getRedirectUrlMultiselect } from "@/api/autocomplete/api";

/**
 * Registers global callback functions on React pages that legacy popup windows
 * call via `window.opener.*`. These are normally provided by `Index.Master.js`,
 * which is intentionally excluded from React pages.
 *
 * Globals registered:
 *  - `lookupCallback`         — LookupTable.js row selection → GetRedirectUrl → navigate
 *  - `gridRowSelected`        — CompanyMaintLookup row selection → GetRedirectUrl → navigate
 *  - `im_salesorcontacts`     — GridTreeView.js context menu → navigate to customer page
 *  - `doMyContacts`           — navigate to customer dashboard
 *  - `showSales`              — navigate to customer sales page
 *  - `showCustomerDetails`    — navigate to customer details page
 *  - `openAddTaskWindow`      — open AddTaskPopup child window; stores checkedValues for getSelectedRows
 *  - `getSelectedRows`        — returns the last checkedValues passed to openAddTaskWindow (multi-select)
 *  - `openEnquiryWindowFromAlert` — open enquiry popup from an alert
 *
 * Call once at the root of the React app (AppShell) where routes are available.
 */
// Mirrors VectaUtil.htmlDecode (jQuery $('<div/>').html(v).text() trick)
function htmlDecode(value: string): string {
  const txt = document.createElement("textarea");
  txt.innerHTML = value;
  return txt.value;
}

export function useWindowOpenerCallbacks(routes: AppRoutes | undefined) {
  useEffect(() => {
    if (!routes) return;

    (window as any).gridRowSelected = (code: string, categoryAndIndex: string) => {
      window.onbeforeunload = null;
      const decodedCode = htmlDecode(code);
      const decodedCatIdx = htmlDecode(categoryAndIndex);
      const parts = decodedCatIdx.split("|");
      const category = parts[0];
      const index = parts[1];
      const description = parts[2];

      let target = category;
      let data = "";

      switch (category) {
        case "Company":
          data = "CompanyAccount";
          break;
        case "CompanyCrm":
          data = index;
          break;
        case "Product":
          break;
        case "ProdAnal":
          data = `Analysis|Product|${index}|${description}`;
          break;
        case "CompAnal":
          data = `Analysis|Company|${index}|${description}`;
          break;
        case "HistAnal":
          data = `Analysis|History|${index}|${description}`;
          break;
        case "ProdGrp":
          target = "Product Group";
          break;
        case "HistUD":
          data = `UD|History|${index}|${description}`;
          break;
      }

      if (category !== "") {
        getRedirectUrl(decodedCode, target, data)
          .then((url) => { if (url) window.location.assign(routes.root + url); })
          .catch(() => {});
      }
    };

    (window as any).doMyContacts = (id: string) => {
      window.onbeforeunload = null;
      window.location.assign(`${routes.dashboard}?customerid=${id}`);
    };

    (window as any).showSales = (id: string) => {
      window.onbeforeunload = null;
      window.location.assign(`${routes.sales}?customerid=${id}&target=Sales.aspx`);
    };

    (window as any).showCustomerDetails = (id: string) => {
      window.onbeforeunload = null;
      window.location.assign(`${routes.dashboard}?customerid=${id}&target=Details.aspx`);
    };

    // Called from GridTreeView.js gvt_tellMaster.
    // "Sales" passes JSON.stringify(salesDetailsModel) — extract Code from it.
    // "Tasks" / "Details" pass a plain account code string.
    (window as any).im_salesorcontacts = (who: string, coyCode: string) => {
      let accountCode: string;
      let targetPage: string;
      console.log("im_salesorcontacts", who, coyCode);
      switch (who) {
        case "Sales": {
          try {
            const model = JSON.parse(coyCode) as { Code: string };
            accountCode = model.Code;
          } catch {
            return;
          }
          targetPage = "Sales.aspx";
          break;
        }
        case "Tasks":
          accountCode = coyCode;
          targetPage = "TasksList.aspx";
          break;
        default:
          accountCode = coyCode;
          targetPage = "Details.aspx";
          break;
      }

      console.log("im_salesorcontacts redirecting to", routes.dashboard, accountCode, targetPage);
      window.location.assign(
        `${routes.dashboard}?customercode=${encodeURIComponent(accountCode)}&target=${targetPage}`,
      );
    };

    // Mirrors Index.Master.js checkedRows / getSelectedRows pattern so that
    // AddTaskPopup.aspx can retrieve the full selection for multi-select tasks.
    let checkedRows: Array<{ AccountNo: string; ContactId?: string | null }> | null = null;

    (window as any).getSelectedRows = () => checkedRows;

    (window as any).openAddTaskWindow = (
      checkedValues: Array<{ AccountNo: string; ContactId?: string | null }> | null,
      parameters: string,
      parent?: Window,
    ): Window | null => {
      checkedRows = checkedValues;
      let params = parameters;
      if (checkedValues != null) {
        if (checkedValues.length === 1) {
          params += "&AccountNo=" + encodeURIComponent(checkedValues[0].AccountNo);
          if (checkedValues[0].ContactId) {
            params += "&contactid=" + encodeURIComponent(checkedValues[0].ContactId);
          }
        } else {
          params += "&Count=" + checkedValues.length;
        }
      }
      const opener = parent ?? window;
      return opener.open(
        `${routes.addTaskPopup}?${params}`,
        "AddTaskPopup",
        "width=1024,height=768,toolbar=no,location=no,menubar=no,resizable=yes,scrollbars=yes",
      ) ?? null;
    };

    (window as any).openEnquiryWindowFromAlert = (url: string, enquiryType: string) => {
      popupCenter(`${url}?request=loadFromAlert&theID=${enquiryType}`, "vectaEnquiry", 1024, 768);
    };

    // Stub for userPref.js closeAndReload: window.opener.Loader.block(...) then reload.
    // The real Loader is a legacy jQuery UI utility not present on React pages.
    (window as any).Loader = {
      block: () => {},
      unblock: () => {},
    };

    (window as any).lookupCallback = (paras: string, id: string | string[]) => {
      console.log("lookupCallback", paras, id);
      window.onbeforeunload = null;
      const isMulti = Array.isArray(id);

      const redirect = isMulti
        ? getRedirectUrlMultiselect(id as string[], paras, "")
        : getRedirectUrl(id as string, paras, "");

      redirect
        .then((url) => {
          console.log("lookupCallback result", url);
          if (url) {
            console.log("lookupCallback redirecting to", routes.root + url);
            window.location.assign(routes.root + url);
          }
        })
        .catch((error) => {
          console.error("lookupCallback error", error);
        });
    };

    return () => {
      delete (window as any).gridRowSelected;
      delete (window as any).showSales;
      delete (window as any).showCustomerDetails;
      delete (window as any).im_salesorcontacts;
      delete (window as any).doMyContacts;
      delete (window as any).openEnquiryWindowFromAlert;
      delete (window as any).openAddTaskWindow;
      delete (window as any).getSelectedRows;
      delete (window as any).doMyContacts;
      delete (window as any).lookupCallback;
      delete (window as any).Loader;
    };
  }, [routes]);
}
